import { useState } from 'react';

import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSmsActionLogs } from '@/hooks/useSmsActionConfigs';
import { formatDateTime } from '@/utils/format';

interface SmsActionLogsProps {
  configId: string;
  onBack: () => void;
}

const STATUS_COLORS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  PENDING: 'outline',
  REPLIED: 'secondary',
  TIMED_OUT: 'destructive',
  ACTION_SUCCESS: 'default',
  ACTION_FAILED: 'destructive',
  ERROR: 'destructive',
};

export const SmsActionLogs = ({ configId, onBack }: SmsActionLogsProps) => {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useSmsActionLogs(configId, page);

  return (
    <div className="space-y-8">
      <header className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">SMS Actions</p>
          <h1 className="text-3xl font-bold tracking-tight">Action Logs</h1>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Logs</CardTitle>
          <CardDescription>{data ? `${data.total} total log(s)` : 'Loading...'}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
            </div>
          ) : !data || data.data.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">No logs yet</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-left font-medium">Phone</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Sent At</th>
                      <th className="px-3 py-2 text-left font-medium">Reply</th>
                      <th className="px-3 py-2 text-left font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((log) => (
                      <tr key={log.id} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <Badge variant={STATUS_COLORS[log.status] || 'outline'}>
                            {log.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{log.contact_phone}</td>
                        <td className="px-3 py-2">{log.message_type}</td>
                        <td className="px-3 py-2">{formatDateTime(log.sent_at)}</td>
                        <td className="max-w-[200px] truncate px-3 py-2">
                          {log.inbound_message || '-'}
                        </td>
                        <td className="px-3 py-2">
                          {log.action_result
                            ? (log.action_result as { reasoning?: string })?.reasoning ||
                              JSON.stringify(log.action_result).slice(0, 50)
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    Page {data.page} of {data.totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= data.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
